import { useEffect, useState } from 'react';

/**
 * Fetches binary content and hands back an object URL, revoking it on unmount.
 *
 * An object URL pins its blob in memory until `revokeObjectURL` is called — the garbage collector
 * cannot reclaim it, because the URL is a live reference. So the component that creates one has to
 * own it, and that is why this is a hook per image rather than a map of URLs in a parent: a map
 * leaks every entry whose row was removed.
 *
 * Two cases the obvious version gets wrong, both handled below:
 *
 *  - The fetch resolving AFTER unmount. Cleanup has already run and will not run again for this
 *    URL, so the URL is revoked at the point it arrives instead.
 *  - A failure. There is nothing useful to say about one missing thumbnail and a toast per tile
 *    would be worse than the gap, so it resolves to null and renders as the placeholder.
 */
export function useRevokableObjectUrl(
  load: (signal: AbortSignal) => Promise<string>,
  deps: unknown[],
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let created: string | null = null;
    let cancelled = false;

    load(controller.signal)
      .then((objectUrl) => {
        created = objectUrl;
        if (cancelled) URL.revokeObjectURL(objectUrl);
        else setUrl(objectUrl);
      })
      .catch(() => {
        /* renders as the placeholder — see above */
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (created) URL.revokeObjectURL(created);
      setUrl(null);
    };
    // The caller states the dependencies, because `load` is a fresh closure on every render and
    // including it would refetch the image on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return url;
}
